import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Email as EmailIcon,
  Send as SendIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';

const ShareModal = ({
  open,
  onClose,
  item,      // Renamed from 'question' - can be either a question or business update
  itemType,  // New prop: 'question' or 'update' to identify content type
  followUps = [],
  onShare,
  user
}) => {
  const [formData, setFormData] = useState({
    recipientEmail: '',
    recipientName: '',
    customMessage: '',
    includeFeedback: false,
    includeFollowUps: true,
    includeNotes: false
  });
  const [isSharing, setIsSharing] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (!open) {
      setFormData({
        recipientEmail: '',
        recipientName: '',
        customMessage: '',
        includeFeedback: false,
        includeFollowUps: true,
        includeNotes: false
      });
      setErrors({});
    }
  }, [open]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.recipientEmail.trim()) {
      newErrors.recipientEmail = 'Recipient email is required';
    } else if (!validateEmail(formData.recipientEmail)) {
      newErrors.recipientEmail = 'Please enter a valid email address';
    }

    if (!formData.recipientName.trim()) {
      newErrors.recipientName = 'Recipient name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleShare = async () => {
    if (!validateForm()) {
        return;
    }
    setIsSharing(true);
    try {
        let shareData;
        
        if (itemType === 'update') {
    // For SavvaiSignals™
    shareData = {
        updateData: {
            id: item.id,
            title: item.title,
            content: item.content,
            sources: item.sources || [],
            followUps: formData.includeFollowUps ? followUps.map(followUp => ({
                id: followUp.id,
                questionText: followUp.questionText,
                answer: followUp.answer,
                createdAt: followUp.createdAt
            })) : []
        },
        recipientEmail: formData.recipientEmail.trim(),
        recipientName: formData.recipientName.trim(),
        personalMessage: formData.customMessage.trim(),
        senderName: user?.displayName || user?.email || 'Savvai User'
    };
} else if (itemType === 'ams_report') {
    // NEW: Handle AMS Reports
    shareData = {
        reportData: {
            metadata: item.metadata,
            reportText: item.reportText,
        },
        recipientEmail: formData.recipientEmail.trim(),
        personalMessage: formData.customMessage.trim(),
    };
} else {
    // For Questions (existing code - unchanged)
    const questionThread = {
        mainQuestion: {
            id: item.id,
            questionText: item.questionText,
            answer: item.answer,
            status: item.status,
            createdAt: item.createdAt,
            clientName: item.clientName,
            category: item.category,
            userNotes: formData.includeNotes ? item.userNotes : null
        },
        followUps: formData.includeFollowUps ? followUps.map(followUp => ({
            id: followUp.id,
            questionText: followUp.questionText,
            answer: followUp.answer,
            createdAt: followUp.createdAt
        })) : [],
        feedback: formData.includeFeedback ? {
            rating: item.feedback?.rating,
            comment: item.feedback?.comment,
            submittedAt: item.feedback?.submittedAt
        } : null
    };
    
    shareData = {
        questionThread,
        recipientEmail: formData.recipientEmail.trim(),
        recipientName: formData.recipientName.trim(),
        personalMessage: formData.customMessage.trim(),
        senderName: user?.displayName || user?.email || 'Savvai User',
        includeFeedback: formData.includeFeedback,
        includeFollowUps: formData.includeFollowUps,
        includeNotes: formData.includeNotes
    };
}

        await onShare(shareData);
        toast.success(`Content shared successfully with ${formData.recipientName}!`);
        onClose();
    } catch (error) {
        console.error('Error sharing content:', error);
        toast.error('Failed to share content. Please try again.');
    } finally {
        setIsSharing(false);
    }
};

  const hasFollowUps = followUps && followUps.length > 0;
  const hasFeedback = itemType === 'question' && (item?.feedback?.rating || item?.feedback?.comment);
  const hasNotes = itemType === 'question' && (item?.userNotes && item.userNotes.trim().length > 0);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon color="primary" />
          <Typography variant="h6" component="span">
    {itemType === 'ams_report'
        ? 'Share Chat Summary'
        : itemType === 'update'
        ? 'Share SavvaiSignals'
        : 'Share Question Thread'}
</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
    {
        itemType === 'ams_report'
            ? 'Share this Archive Master Summary via email. The recipient will receive a formatted email with the report.'
            : itemType === 'update'
            ? 'Share this SavvaiSignals via email. The recipient will receive a formatted email with the selected content.'
            : 'Share this question and answer thread via email. The recipient will receive a formatted email with the selected content.'
    }
</Typography>

          {/* Item Preview */}
          {itemType !== 'ams_report' && (
    <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {itemType === 'update' ? 'Sharing SavvaiSignals:' : 'Sharing Question:'}
        </Typography>
        <Typography variant="body2" sx={{
            fontStyle: 'italic',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
        }}>
            "{itemType === 'update' ? item?.title : item?.questionText}"
        </Typography>
        {item?.clientName && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Client: {item.clientName}
            </Typography>
        )}
    </Alert>
)}
        </Box>

        {/* Recipient Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Recipient Information
          </Typography>
          
          <TextField
            fullWidth
            label="Recipient Email *"
            type="email"
            value={formData.recipientEmail}
            onChange={handleInputChange('recipientEmail')}
            error={!!errors.recipientEmail}
            helperText={errors.recipientEmail}
            sx={{ mb: 2 }}
            disabled={isSharing}
          />

          <TextField
            fullWidth
            label="Recipient Name *"
            value={formData.recipientName}
            onChange={handleInputChange('recipientName')}
            error={!!errors.recipientName}
            helperText={errors.recipientName}
            sx={{ mb: 2 }}
            disabled={isSharing}
          />

          <TextField
            fullWidth
            label="Custom Message (Optional)"
            multiline
            rows={3}
            value={formData.customMessage}
            onChange={handleInputChange('customMessage')}
            placeholder="Add a personal message to include with the shared content..."
            disabled={isSharing}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Content Options */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
            Content to Include
          </Typography>

          {hasFollowUps && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.includeFollowUps}
                  onChange={handleInputChange('includeFollowUps')}
                  disabled={isSharing}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Include Follow-up Questions ({followUps.length})
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Include all follow-up questions and their answers in the shared email
                  </Typography>
                </Box>
              }
              sx={{ mb: 1, alignItems: 'flex-start' }}
            />
          )}

          {hasFeedback && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.includeFeedback}
                  onChange={handleInputChange('includeFeedback')}
                  disabled={isSharing}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Include Your Feedback
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Include your star rating and feedback comments
                  </Typography>
                </Box>
              }
              sx={{ mb: 1, alignItems: 'flex-start' }}
            />
          )}

          {hasNotes && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.includeNotes}
                  onChange={handleInputChange('includeNotes')}
                  disabled={isSharing}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">
                    Include Your Private Notes
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Include your personal notes about this question
                  </Typography>
                </Box>
              }
              sx={{ mb: 1, alignItems: 'flex-start' }}
            />
          )}

          {!hasFollowUps && !hasFeedback && !hasNotes && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No additional content available to include (no follow-ups, feedback, or notes)
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={onClose} 
          disabled={isSharing}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={handleShare}
          disabled={isSharing}
          variant="contained"
          startIcon={isSharing ? <CircularProgress size={16} /> : <SendIcon />}
          sx={{ minWidth: 120 }}
        >
          {isSharing ? 'Sharing...' : 'Send Email'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ShareModal;